import Boss from '../../ui/appsIcons/Boss';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../../ui/Button';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import { loadSlim } from '@tsparticles/slim';
import ParticlesWrapper from './Particles';
import { initParticlesEngine } from '@tsparticles/react';
import { usePopupAccordion } from './PopupAccordionContext';
import { EmpreenderApps } from '../../lib/AppsArray';

export type popUpStyles = {
	bg_color?: string;
	text_color?: string;
	borderColor?: string;
	iconBg?: string;
	iconColor?: string;
	appBackground?: string;
	textHeader?: string;
	blurColor?: string;
	footerBg?: string;
	footerColor?: string;
	btnbg?: string;
};

export type PopupData = {
	id: number;
	header_title: string;
	icon?: string;
	title: string;
	subtitle: string;
	app?: string;
	youtube_url?: string;
	styles?: popUpStyles;
	isEvent?: boolean;
	button: {
		text: string;
		link: string;
	};
	footer: {
		text: string;
		link_text: string;
		link_url: string;
	};
	particles_image: string;
	use_particles: string;
	open?: string;
};

const getYoutubeEmbedUrl = (url?: string) => {
	if (!url) return null;

	try {
		const parsedUrl = new URL(url);


		if (parsedUrl.hostname.includes('youtube.com')) {
			const videoId = parsedUrl.searchParams.get('v');
			return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
		}

		if (parsedUrl.hostname.includes('youtu.be')) {
			const videoId = parsedUrl.pathname.replace('/', '');
			return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
		}

		return null;
	} catch (err) {
		console.log(err);
		return null;
	}
};

const AppHeader = ({
	isOpen,
	onClick,
	headerTitle,
	app,
	icon,
	styles,
}: {
	isOpen: boolean;
	onClick: () => void;
	headerTitle: string;
	app?: string;
	icon?: string;
	styles?: popUpStyles;
}) => {

	const matchApp = EmpreenderApps.find((a) => a.tags.some((tag) => tag.toLowerCase() === app?.toLowerCase()));
	// console.log(`close? ${close}`);
	return (
		<div className='flex flex-row items-center gap-3 cursor-pointer select-none relative z-10 pt-2 pb-2' onClick={onClick}>
			<div className='icone-popup-pagamento bg-white rounded-lg p-1' style={{ backgroundColor: styles?.appBackground }} draggable={false}>
				{
					icon ? <img className='w-[30px] object-cover' src={icon} /> : matchApp ? matchApp.icon : <Boss />
				}
			</div>
			<div className='w-full flex flex-col'>
				<p className='text-white text-sm font-medium max-[390px]:text-[11px] relative top-[-1px]' style={{ color: styles?.textHeader }}>{headerTitle}</p>
			</div>
			<div className='h-8'>
				<div className={twMerge('flex-shrink-0 bg-gray-900 rounded-lg size-7 flex items-center justify-center text-white', ``)} style={{ backgroundColor: styles?.iconBg, color: styles?.iconColor }} draggable={false}>
					<motion.div initial={{ rotate: 180 }} animate={{ rotate: isOpen ? 180 : 0 }}>
						<ChevronDown className='size-4 text-[currentColor]' />
					</motion.div>
				</div>
			</div>
		</div>
	);
};
const Footer = ({
	isOpen,
	text,
	linkText,
	linkUrl,
	styles,
	close,
	onNaoMostrar,
}: {
	isOpen: boolean;
	text: string;
	linkText: string;
	linkUrl: string;
	styles?: popUpStyles;
	close?: boolean;
	onNaoMostrar?: () => void;
}) => {
	return (
		<motion.div
			initial={{ padding: '0px', height: 0, opacity: 0 }}
			animate={{ padding: isOpen ? '0px' : '24px', height: isOpen ? 0 : 80, opacity: isOpen ? 0 : 1 }}
			className={twMerge('relative z-10 text-center p-6 bg-gray-950 flex items-center justify-center')} style={{ backgroundColor: styles?.footerBg, pointerEvents: isOpen ? 'none' : 'auto' }}
		>
			<p className={twMerge('text-gray-300 text-xs font-normal flex flex-col select-none', ``)} style={{ color: styles?.footerColor }}>
				{!close && text}

				{close && <div className='underline cursor-pointer' onClick={onNaoMostrar}>
					Não mostrar mais
				</div>}

				{!close &&
					<div>
						<a target='_blank' href={linkUrl} className='underline underline-offset-[2.4px]' rel='noreferrer'>
							{linkText}
						</a>
						.
					</div>
				}
			</p>
		</motion.div>
	);
};

const Blur = ({ styles }: { styles?: popUpStyles; }) => {
	return (
		<div
			className={twMerge(
				'absolute bottom-[-59px] z-0 w-[183px] h-[86px] bg-[#8346F9] !pointer-events-none !select-none rounded-[183px] blur-[80px]',
				``
			)}
			style={{ backgroundColor: styles?.blurColor }}
			draggable={false}
		></div>
	);
};
const BodyPopUp = ({
	isOpen,
	title,
	subtitle,
	buttonText,
	buttonLink,
	styles,
	youtubeUrl,
	onNaoMostrar,
}: {
	isOpen: boolean;
	title: string;
	subtitle: string;
	buttonText: string;
	buttonLink: string;
	styles?: popUpStyles;
	youtubeUrl?: string;
	onNaoMostrar?: () => void;
}) => {
	const embedUrl = getYoutubeEmbedUrl(youtubeUrl);

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: isOpen ? 0 : 1 }}
			className={twMerge(`w-full h-[calc(100%-32px)] flex flex-col ${isOpen ? 'pointer-events-none' : ''}`)}
		>
			<div className='relative h-full flex flex-col items-center justify-center gap-[32px]' style={{ gap: youtubeUrl ? 15 : 32 }}>
				<div className='relative z-10 text-center w-[295px] flex flex-col gap-3 items-center justify-center select-none'>
					<h1
						className='text-gray-50 w-[auto] text-2xl font-semibold tracking-[-0.48px]'
						style={{ color: styles?.text_color }}
					>
						{title}
					</h1>

					{embedUrl ? (
						<div className='w-[325px] h-[183px] overflow-hidden rounded-xl'>
							<iframe
								className='w-full h-full'
								src={embedUrl}
								title={title}
								allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
								referrerPolicy='strict-origin-when-cross-origin'
								allowFullScreen
							/>
						</div>
					) : (
						<span
							className='text-white w-[325px] text-sm font-normal flex items-center'
							style={{ color: styles?.text_color }}
						>
							{subtitle}
						</span>
					)}
				</div>

				<div className='relative z-10 w-full flex flex-col items-center justify-center gap-1'>
					<Button asChild className='bg-white text-gray-900 hover:bg-gray-100 transition-colors rounded-full px-6 py-3 h-10 flex flex-row gap-[2px]' style={{ backgroundColor: styles?.btnbg }}>
						<a target='_blank' href={buttonLink} draggable={false} rel='noreferrer'>
							<span>{buttonText}</span> <ChevronRight className='size-[18px]' />
						</a>
					</Button>

					{onNaoMostrar && (
						<Button
							variant='link'
							onClick={onNaoMostrar}
							className='h-auto font-normal text-white/70'
						>
							Não mostrar mais
						</Button>
					)}
				</div>
				<Blur styles={styles} />
			</div>
		</motion.div>
	);
};

const PopUpPagamento = ({
	id,
	data,
	onHidePopup,
}: {
	id: number;
	data: PopupData;
	onHidePopup?: (id: number) => void;
}) => {
	const { styles, use_particles } = data;

	const { openId, toggle } = usePopupAccordion();

	const isCollapsed = openId !== id;

	const [init, setInit] = useState(false);

	const usaParticles = use_particles === 'true';

	useEffect(() => {
		initParticlesEngine(async (engine) => {
			await loadSlim(engine);
		}).then(() => {
			setInit(true);
		});
	}, []);

	// Um dono só para o descarte: o rodapé usa nos popups de evento, o corpo usa no trial e no
	// expirado. Quem some com o card é o Stacking, via onHidePopup.
	const naoMostrarMais = () => {
		localStorage.setItem(`notshow_notification_${data.id}`, `true`);
		onHidePopup?.(data.id);
	};

	const handleToggle = () => {
		toggle(id);
	};

	return (
		<motion.div
			initial={{ x: 400 }}
			animate={{ x: 0 }}
			transition={{
				type: 'spring',
				visualDuration: 0.2,
				bounce: 0.2,
			}}
			className='popup-empreender-avisos overflow-hidden w-[379px] h-auto max-[390px]:w-11/12 rounded-xl border border-gray-900 bg-gray-950'
			style={{ borderColor: styles?.borderColor }}
		>
			<motion.div
				initial={{ height: 48 }}
				animate={{ height: isCollapsed ? 48 : 379 }}
				className='h-[calc(100%-80px)] p-2 bg-gray-950 rounded-[1px] !pt-0'
				style={{ backgroundColor: styles?.bg_color }}
			>
				<motion.div
					className='absolute top-0 left-0 size-full z-0 pointer-events-none'
					initial={{ opacity: 0 }}
					animate={{ opacity: isCollapsed ? 0 : 1 }}
				>
					{usaParticles && init && <ParticlesWrapper id={`tsparticles-${id}`} imgSrc={data.particles_image} />}
				</motion.div>

				<AppHeader
					onClick={handleToggle}
					isOpen={isCollapsed}
					headerTitle={data.header_title}
					icon={data.icon}
					app={data.app}
					styles={styles}
				/>

				<BodyPopUp
					isOpen={isCollapsed}
					title={data.title}
					subtitle={data.subtitle}
					buttonText={data.button.text}
					buttonLink={data.button.link}
					styles={styles}
					youtubeUrl={data.youtube_url}
					onNaoMostrar={data.isEvent ? undefined : naoMostrarMais}
				/>
			</motion.div>

			<Footer
				isOpen={isCollapsed}
				text={data.footer.text}
				linkText={data.footer.link_text}
				linkUrl={data.footer.link_url}
				close={data.isEvent}
				styles={styles}
				onNaoMostrar={naoMostrarMais}
			/>
		</motion.div>
	);
};

export default PopUpPagamento;